import { useCometMcpServers } from '#pplx/frontend/apps/ask/comet-browser/hooks/useCometMcpServers.js';
import { SourceIcon } from '#pplx/frontend/apps/ask/sources/components/SourceIcon.js';
import { useSourceLimits } from '#pplx/frontend/apps/ask/sources/hooks/useSourceLimits.js';
import { useSources } from '#pplx/frontend/apps/ask/sources/hooks/useSources.js';
import { useSuggestedSources } from '#pplx/frontend/apps/ask/sources/hooks/useSuggestedSources.js';
import { getSourceMentionOptions } from '#pplx/frontend/apps/ask/sources/utilities/getSourceMentionOptions.js';
import { useSourceActivity } from '#pplx/frontend/apps/ask/user-settings/hooks/useSourceActivity.js';
import { useDebouncedValue } from '#pplx/frontend/common/hooks/useDebouncedValue.js';
import { createElement, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
export function useAtMentionSources({ isLazy = true, enabled = true, onMentionSource, searchQuery, productSurface, }) {
    const { $t } = useIntl();
    const [isTriggered, setIsTriggered] = useState(() => !isLazy);
    const { cometMcpSources } = useCometMcpServers();
    const { getSourceLimited } = useSourceLimits();
    const debouncedSearchQuery = useDebouncedValue(searchQuery, 300);
    // Lazy mention menus must not issue the source query until first triggered.
    const queryEnabled = enabled && (!isLazy || isTriggered);
    const { sources, isSourceConnected, getSourceLabel, getSource } = useSources({
        enabled: queryEnabled,
        searchQuery: debouncedSearchQuery,
        productSurface,
    });
    const { trackSourceActivity } = useSourceActivity();
    const { suggested: suggestedSources, refresh: refreshSuggestions } = useSuggestedSources({
        enabled: queryEnabled,
        productSurface,
    });
    const handleMention = useCallback((sourceType) => {
        onMentionSource?.(sourceType);
        trackSourceActivity(sourceType);
    }, [onMentionSource, trackSourceActivity]);
    const mentionSourcesOptions = useMemo(() => getSourceMentionOptions({
        sources,
        suggestedSources,
        isSourceConnected,
        getSource,
        getSourceLabel,
        getSourceLimited,
        cometMcpSources,
        handleMention,
        $t,
    }), [
        sources,
        suggestedSources,
        isSourceConnected,
        getSource,
        getSourceLabel,
        getSourceLimited,
        cometMcpSources,
        handleMention,
        $t,
    ]);
    const onTrigger = useCallback(() => {
        if (!isTriggered) {
            setIsTriggered(true);
        }
        refreshSuggestions();
    }, [isTriggered, refreshSuggestions]);
    const mentionIconRenderer = useCallback((icon) => {
        if (icon.type !== 'source') {
            return undefined;
        }
        return createElement(SourceIcon, { sourceId: icon.sourceId, size: 'small' });
    }, []);
    return useMemo(() => ({
        isAtMentionSourcesEnabled: enabled,
        mentionSourcesOptions,
        mentionIconRenderer,
        onTrigger,
    }), [enabled, mentionSourcesOptions, mentionIconRenderer, onTrigger]);
}
//# sourceMappingURL=useAtMentionSources.js.map